import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { IApprovalRequest } from '../../definitions/Approval'
import Formatter from '../../helpers/Formatter'
import { Close, BlockRounded, ApprovalRounded } from '@mui/icons-material'
import algo from '../../core/Algo'
import ApprovalManager from '../../managers/ApprovalManager'
import { LoadingButton } from '@mui/lab'

interface P {
    open: boolean
    onClose: () => void
    approval: IApprovalRequest
    onApprove: () => Promise<void>
    onReject: (reason: string) => void
    rejectStatus
    approveStatus?: string
}

const ApprovalModal = ({ open, onClose, approval, onApprove, onReject, rejectStatus, approveStatus }: P) => {
    const [showRejectionField, setShowRejectionField] = useState(false)
    const [rejectionReason, setRejectionReason] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (rejectStatus === 'success') {
            setShowRejectionField(false)
            setRejectionReason('')
        }
    }, [rejectStatus])

    const onClickApprove = async () => {
        setIsSubmitting(true)
        await onApprove()
        setIsSubmitting(false)
    }

    const handleClose = () => {
        setShowRejectionField(false)
        setRejectionReason('')
        onClose()
    }

    const handleReject = () => {
        if (!algo.hasVal(rejectionReason)) return
        onReject(rejectionReason)
    }

    const renderDialogContent = () => {
        if (!approval) return null

        const lastChange = approval.changes.length > 0 ? approval.changes[approval.changes.length - 1] : null

        switch (approval.status) {
            case 'ready-for-review':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>{!showRejectionField ? `Review ${approval?.entityType} for approval` : `Reject ${approval?.entityType}`}</Typography>
                                <IconButton onClick={handleClose} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={2}>
                                {ApprovalManager.statusField(approval.status)}
                                {ApprovalManager.infoField('Name', approval.entityName)}
                                {ApprovalManager.infoField('Requested By', lastChange.userName ?? 'N/A')}
                                {ApprovalManager.infoField('Requested At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                {showRejectionField && (
                                    <Box>
                                        <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                                            Rejection Reason *
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={8}
                                            maxRows={10}
                                            value={rejectionReason}
                                            onChange={e => setRejectionReason(e.target.value)}
                                            placeholder='Please provide a reason for rejection...'
                                            error={showRejectionField && !rejectionReason.trim()}
                                            helperText={showRejectionField && !rejectionReason.trim() ? 'Rejection reason is required' : ''}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ pb: 2 }}>
                            {!showRejectionField
                                ? (
                                        <>
                                            <Button
                                                startIcon={<BlockRounded />}
                                                variant='contained'
                                                color='error'
                                                onClick={() => setShowRejectionField(true)}
                                                disabled={isSubmitting}>
                                                Reject
                                            </Button>
                                            <LoadingButton
                                                loading={approveStatus === 'loading'}
                                                startIcon={<ApprovalRounded />}
                                                variant='contained'
                                                onClick={onClickApprove}
                                                loadingPosition='start'
                                                disabled={isSubmitting}>
                                                Approve
                                            </LoadingButton>
                                        </>
                                    )
                                : (
                                        <>
                                            <Button
                                                variant='outlined'
                                                onClick={() => {
                                                    setShowRejectionField(false)
                                                    setRejectionReason('')
                                                }}
                                                disabled={isSubmitting}>
                                                Back
                                            </Button>
                                            <LoadingButton
                                                startIcon={<BlockRounded />}
                                                variant='contained'
                                                loadingPosition='start'
                                                loading={rejectStatus === 'loading'}
                                                color='error'
                                                onClick={handleReject}
                                                disabled={isSubmitting ?? !rejectionReason.trim()}>
                                                Reject
                                            </LoadingButton>
                                        </>
                                    )}
                        </DialogActions>
                    </>
                )
            case 'approved':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>Approved {approval?.entityType}</Typography>
                                <IconButton onClick={handleClose} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={4} mt={2}>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.statusField(approval.status)}
                                    {ApprovalManager.infoField('Name', approval.entityName)}
                                </Stack>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.infoField('Approved By', lastChange.userName ?? 'N/A')}
                                    {ApprovalManager.infoField('Approved At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                </Stack>
                            </Stack>
                        </DialogContent>
                    </>
                )
            default:
                return null
        }
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2, minHeight: 300 } }}>
            {renderDialogContent()}
        </Dialog>
    )
}

export default ApprovalModal
